"use client";

import { useEffect, useRef, type RefObject } from "react";
import { escapeHtml, escapeHtmlAttribute } from "@/utils/common/html";

/**
 * Mention markup stored by VariableEditor: `@[Display](id)`.
 * The id may contain one level of nested parentheses. VariableEditor imports
 * this pattern for highlighting, so copy and display stay on the same tokens.
 * Create a fresh global regex per scan — a shared one would leak `lastIndex`.
 */
export const VARIABLE_MARKUP_PATTERN =
  /@\[([^\]]+)\]\(((?:[^)(]|\([^)(]*\))+)\)/;

export const VARIABLE_CLIPBOARD_MIME = "text/react-mentions";

const MENTION_ATTRIBUTE = "data-gail-mention";

export type VariableClipboardPayload = {
  "text/plain": string;
  "text/html": string;
  "text/react-mentions": string;
};

export type MarkupSlice = {
  markup: string;
  markupStart: number;
  markupEnd: number;
};

type Segment = {
  kind: "text" | "mention";
  markupStart: number;
  markupEnd: number;
  plainStart: number;
  plainEnd: number;
  plain: string;
};

type ClipboardReader = {
  getData: (type: string) => string;
};

type ClipboardWriter = {
  setData: (type: string, value: string) => void;
};

const markupRegex = (): RegExp => new RegExp(VARIABLE_MARKUP_PATTERN.source, "g");

const segmentsOf = (markup: string): Segment[] => {
  const segments: Segment[] = [];
  let markupCursor = 0;
  let plainCursor = 0;

  for (const match of markup.matchAll(markupRegex())) {
    const index = match.index ?? 0;
    if (index > markupCursor) {
      const plain = markup.slice(markupCursor, index);
      segments.push({
        kind: "text",
        markupStart: markupCursor,
        markupEnd: index,
        plainStart: plainCursor,
        plainEnd: plainCursor + plain.length,
        plain,
      });
      plainCursor += plain.length;
    }

    const display = match[1] ?? "";
    const token = match[0];
    segments.push({
      kind: "mention",
      markupStart: index,
      markupEnd: index + token.length,
      plainStart: plainCursor,
      plainEnd: plainCursor + display.length,
      plain: display,
    });
    plainCursor += display.length;
    markupCursor = index + token.length;
  }

  if (markupCursor < markup.length) {
    const plain = markup.slice(markupCursor);
    segments.push({
      kind: "text",
      markupStart: markupCursor,
      markupEnd: markup.length,
      plainStart: plainCursor,
      plainEnd: plainCursor + plain.length,
      plain,
    });
  }

  return segments;
};

export const plainTextFromMarkup = (markup: string): string =>
  segmentsOf(markup)
    .map((segment) => segment.plain)
    .join("");

const clampIndex = (index: number, length: number): number =>
  Math.min(Math.max(index, 0), length);

const caretSlice = (markupStart: number, markupEnd: number): MarkupSlice => ({
  markup: "",
  markupStart,
  markupEnd,
});

/**
 * A caret is the gap before a character. Only a caret strictly inside a chip
 * (`plainStart < at < plainEnd`) replaces that token, so a paste cannot split
 * `@[Fir|st](id)`. A caret on either edge inserts there, including when the
 * chip is the first thing in the field.
 */
const collapsedRange = (markup: string, index: number): MarkupSlice => {
  const plainLength = plainTextFromMarkup(markup).length;
  const at = clampIndex(index, plainLength);

  for (const segment of segmentsOf(markup)) {
    if (segment.kind === "mention") {
      if (at === segment.plainStart) return caretSlice(segment.markupStart, segment.markupStart);
      if (at > segment.plainStart && at < segment.plainEnd) {
        return {
          markup: markup.slice(segment.markupStart, segment.markupEnd),
          markupStart: segment.markupStart,
          markupEnd: segment.markupEnd,
        };
      }
      continue;
    }
    if (at < segment.plainStart || at >= segment.plainEnd) continue;
    const offset = at - segment.plainStart;
    const markupIndex = segment.markupStart + offset;
    return caretSlice(markupIndex, markupIndex);
  }

  return caretSlice(markup.length, markup.length);
};

const expandedRange = (
  markup: string,
  selectionStart: number,
  selectionEnd: number
): MarkupSlice | null => {
  const plainLength = plainTextFromMarkup(markup).length;
  const start = clampIndex(Math.min(selectionStart, selectionEnd), plainLength);
  const end = clampIndex(Math.max(selectionStart, selectionEnd), plainLength);
  if (start === end) return null;

  const overlapping = segmentsOf(markup).filter(
    (segment) => start < segment.plainEnd && end > segment.plainStart
  );
  if (overlapping.length === 0) return null;

  const first = overlapping.at(0);
  const last = overlapping.at(-1);
  if (!first || !last) return null;
  const markupStart =
    first.kind === "mention"
      ? first.markupStart
      : first.markupStart + (Math.max(start, first.plainStart) - first.plainStart);
  const markupEnd =
    last.kind === "mention"
      ? last.markupEnd
      : last.markupStart + (Math.min(end, last.plainEnd) - last.plainStart);

  return {
    markup: markup.slice(markupStart, markupEnd),
    markupStart,
    markupEnd,
  };
};

/** Plain-text selection mapped onto markup. A partial chip expands to the whole token. */
export const sliceMarkupForPlainSelection = (
  markup: string,
  selectionStart: number,
  selectionEnd: number
): MarkupSlice | null => expandedRange(markup, selectionStart, selectionEnd);

export const buildClipboardPayload = (markupSlice: string): VariableClipboardPayload => {
  const label = escapeHtml(plainTextFromMarkup(markupSlice));
  const encoded = escapeHtmlAttribute(markupSlice);
  return {
    "text/plain": markupSlice,
    "text/html": `<span ${MENTION_ATTRIBUTE}="${encoded}">${label}</span>`,
    "text/react-mentions": markupSlice,
  };
};

const readClipboard = (data: ClipboardReader, type: string): string => {
  try {
    return data.getData(type) ?? "";
  } catch {
    return "";
  }
};

const unescapeHtml = (value: string): string =>
  value
    .replaceAll("&quot;", '"')
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">")
    .replaceAll("&amp;", "&");

const markupFromHtml = (html: string): string => {
  const match = new RegExp(`${MENTION_ATTRIBUTE}="([^"]*)"`).exec(html);
  if (!match) return "";
  return unescapeHtml(match[1] ?? "");
};

/**
 * Prefer the custom mention flavor, then markup stashed on the HTML span,
 * then plain text. Plain text may already be `@[Label](id)` or ordinary words.
 */
export const readPastedVariableText = (data: ClipboardReader): string => {
  const custom = readClipboard(data, VARIABLE_CLIPBOARD_MIME);
  if (custom) return custom.replaceAll("\r", "");

  const fromHtml = markupFromHtml(readClipboard(data, "text/html"));
  if (fromHtml) return fromHtml.replaceAll("\r", "");

  return readClipboard(data, "text/plain").replaceAll("\r", "");
};

export const writeVariableClipboard = (
  clipboard: ClipboardWriter,
  payload: VariableClipboardPayload
): void => {
  clipboard.setData("text/plain", payload["text/plain"]);
  clipboard.setData("text/html", payload["text/html"]);
  clipboard.setData(VARIABLE_CLIPBOARD_MIME, payload["text/react-mentions"]);
};

export const replacePlainSelection = (
  markup: string,
  selectionStart: number,
  selectionEnd: number,
  insertion: string
): { value: string; caret: number } => {
  const range =
    expandedRange(markup, selectionStart, selectionEnd) ??
    collapsedRange(markup, selectionStart);
  const value =
    markup.slice(0, range.markupStart) + insertion + markup.slice(range.markupEnd);
  const caret = plainTextFromMarkup(markup.slice(0, range.markupStart) + insertion).length;
  return { value, caret };
};

/**
 * Capture-phase clipboard handling for a VariableEditor textarea.
 * react-mentions listens on `document` and writes the visible label as
 * `text/plain`, keeping the chip only in `text/react-mentions`, which Safari
 * and the macOS pasteboard drop. Stopping the event here is what lets the
 * markup survive.
 */
export const useVariableClipboard = (
  inputRef: RefObject<HTMLTextAreaElement | null>,
  markup: string,
  disabled: boolean | undefined,
  setMarkup: (next: string) => void
): void => {
  const markupRef = useRef(markup);
  const disabledRef = useRef(!!disabled);
  const setMarkupRef = useRef(setMarkup);
  const pendingCaret = useRef<number | null>(null);

  markupRef.current = markup;
  disabledRef.current = !!disabled;
  setMarkupRef.current = setMarkup;

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const selection = (): { start: number; end: number } => ({
      start: el.selectionStart ?? 0,
      end: el.selectionEnd ?? 0,
    });

    const onCopy = (event: ClipboardEvent) => {
      if (event.target !== el || !event.clipboardData) return;
      const { start, end } = selection();
      const slice = sliceMarkupForPlainSelection(markupRef.current, start, end);
      if (!slice) return;
      event.preventDefault();
      event.stopPropagation();
      writeVariableClipboard(event.clipboardData, buildClipboardPayload(slice.markup));
    };

    const onCut = (event: ClipboardEvent) => {
      if (event.target !== el) return;
      const { start, end } = selection();
      if (disabledRef.current) {
        if (start !== end) {
          event.preventDefault();
          event.stopPropagation();
        }
        return;
      }
      if (!event.clipboardData) return;
      const slice = sliceMarkupForPlainSelection(markupRef.current, start, end);
      if (!slice) return;
      event.preventDefault();
      event.stopPropagation();
      writeVariableClipboard(event.clipboardData, buildClipboardPayload(slice.markup));
      const next = replacePlainSelection(markupRef.current, start, end, "");
      if (next.value === markupRef.current) return;
      pendingCaret.current = next.caret;
      setMarkupRef.current(next.value);
    };

    const onPaste = (event: ClipboardEvent) => {
      if (event.target !== el) return;
      if (disabledRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!event.clipboardData) return;
      event.preventDefault();
      event.stopPropagation();
      const { start, end } = selection();
      const insertion = readPastedVariableText(event.clipboardData);
      const next = replacePlainSelection(markupRef.current, start, end, insertion);
      if (next.value === markupRef.current) {
        el.setSelectionRange(next.caret, next.caret);
        return;
      }
      pendingCaret.current = next.caret;
      setMarkupRef.current(next.value);
    };

    el.addEventListener("copy", onCopy, true);
    el.addEventListener("cut", onCut, true);
    el.addEventListener("paste", onPaste, true);
    return () => {
      el.removeEventListener("copy", onCopy, true);
      el.removeEventListener("cut", onCut, true);
      el.removeEventListener("paste", onPaste, true);
    };
  }, [inputRef]);

  useEffect(() => {
    const el = inputRef.current;
    const caret = pendingCaret.current;
    if (!el || caret === null) return;
    pendingCaret.current = null;
    el.setSelectionRange(caret, caret);
  }, [markup, inputRef]);
};
